import * as React from 'react';
import { connect } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import AutoSuggest from 'react-autosuggest';
import { debounce, escapeRegExp } from 'lodash';
import classnames from 'classnames';

import { searchCity, searchCountry } from 'modules/locations/reducers/worldLocation';

import './input-location.scss';

import type { LocationSuggestionCityOrCountry, TextEntryKeyboardEvent } from 'types';

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['strong']
};

type OwnProps = {
  value: string,
  name?: string,
  placeholder?: string,
  disabled?: boolean,
  id?: string,
  valueType: 'city' | 'country',
  suggestionType: 'fullName' | 'country' | 'city',
  className?: string,
  inputClassName?: string,
  allowsFreeEntry?: boolean,
  onChange: (b: TextEntryKeyboardEvent, a: LocationSuggestionCityOrCountry) => void
};

type Props = {
  dispatch: any
} & OwnProps;

type State = {
  value: string,
  suggestions: Array<LocationSuggestionCityOrCountry>
};

class InputLocation extends React.Component<Props, State> {
  static defaultProps = {
    disabled: false,
    value: '',
    inputClassName: 'input input--is-editable input--width-fixed input--align-left'
  };

  debouncedSearchSuggestions: Function;

  state = {
    value: this.props.value,
    suggestions: []
  };

  constructor(props: Props) {
    super(props);

    this.debouncedSearchSuggestions = debounce(this.searchSuggestions, 500);
  }

  getSuggestionValue = (suggestion: LocationSuggestionCityOrCountry): string => suggestion[this.props.valueType];

  searchSuggestions = ({
    value
  }: {
    value: string
  }) => {
    const { dispatch, valueType } = this.props;
    const search = valueType === 'city' ? searchCity : searchCountry;

    return dispatch(search(value))
      .then((suggestions) => {
        this.setState({
          suggestions
        });
      });
  };

  handleChange = (event: TextEntryKeyboardEvent, {
    newValue
  }: {
    newValue: string
  }) => {
    event.stopPropagation();
    event.preventDefault();

    this.setState({
      value: newValue
    });
  };

  handleBlur = (event: TextEntryKeyboardEvent, {
    highlightedSuggestion
  }: {
    highlightedSuggestion: string | undefined | null
  }) => {
    const { allowsFreeEntry, valueType, onChange } = this.props;

    if (highlightedSuggestion) {
      return;
    }

    if (!allowsFreeEntry) {
      this.setState({
        value: this.props.value // revert changes
      });
    } else if (onChange) {
      // @ts-ignore this is something fishy tho according to prop types
      onChange(event, { [valueType]: this.state.value });
    }
  };

  handleSuggestionSelected = (event: TextEntryKeyboardEvent, {
    suggestion
  }: {
    suggestion: LocationSuggestionCityOrCountry
  }) => {
    event.stopPropagation();
    event.preventDefault();

    const { onChange } = this.props;

    if (onChange) {
      onChange(event, suggestion);
    }
  };

  handleSuggestionsClearRequested = () => {
    this.setState({
      suggestions: []
    });
  };

  renderSuggestion = (suggestion: LocationSuggestionCityOrCountry, {
    query
  }: {
    query: string
  }) => {
    const suggestionLabel = suggestion[this.props.suggestionType];

    const renderedSuggestion = suggestionLabel.replace(new RegExp(escapeRegExp(query), 'i'), '<strong>$&</strong>');

    return (
      <div
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderedSuggestion, SANITIZE_HTML_CONFIG) }}
      />
    );
  };

  render() {
    const { value, suggestions } = this.state;
    const { disabled, name, id, className, inputClassName, placeholder } = this.props;

    const classes = classnames(['input-location', className]);

    return (
      <section className={classes}>
        <AutoSuggest
          id={id} // must be different if you have several AutoSuggest components on a page
          inputProps={{
            className: inputClassName,
            disabled,
            name,
            value,
            placeholder,
            autoComplete: 'ignore-location',
            onChange: this.handleChange,
            onBlur: this.handleBlur
          }}
          highlightFirstSuggestion
          suggestions={suggestions}
          focusInputOnSuggestionClick={false}
          onSuggestionsFetchRequested={this.debouncedSearchSuggestions}
          onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
          onSuggestionSelected={this.handleSuggestionSelected}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
        />
      </section>
    );
  }
}

export default connect()(InputLocation);
